import styles from './ForYou.module.scss';
import { Icon, TicketIcon, Typography } from '@eventbrite/marmalade';
import { EventCardsBucket } from '../EventCardsBucket/EventCardsBucket';
import { LoadingBucket } from '../LoadingBucket';
import { DoLoginEmptyState } from './DoLoginEmptyState';
import { NoEventsEmptyState } from './NoEventsEmptyState';
import { useRecommendations } from './useRecommendations';
import { useSavedEventIds } from '@/common/useSavedEventIds';
import { useClientAuth } from '@/common/useUserPublicId';
import { useMemo } from 'react';

export const ForYou: React.FC = () => {
    const { authState, publicUserId } = useClientAuth();
    const isAuthenticated = authState === 'authenticated';

    const { data, isFetching, isError } = useRecommendations({ enabled: isAuthenticated });
    const buckets = data ?? [];
    const eventIds = useMemo(
        () => buckets.flatMap((bucket) => bucket.events.map((event) => event.id)),
        [buckets],
    );
    const { savedEventIds, isSaveStateReady } = useSavedEventIds(
        publicUserId,
        eventIds,
        isAuthenticated && buckets.length > 0,
    );
    const personalizedBuckets = useMemo(() => {
        if (!isAuthenticated || !isSaveStateReady) {
            return buckets;
        }

        return buckets.map((bucket) => ({
            ...bucket,
            events: bucket.events.map((event) =>
                savedEventIds.has(event.id)
                    ? { ...event, savedByYou: true }
                    : { ...event, savedByYou: false },
            ),
        }));
    }, [buckets, isAuthenticated, isSaveStateReady, savedEventIds]);

    if (authState === 'unknown' || isFetching) {
        return <LoadingBucket />;
    }

    if (!isAuthenticated) {
        return <DoLoginEmptyState />;
    }

    if (!buckets || buckets.length === 0 || isError) {
        return <NoEventsEmptyState />;
    }

    return (
        <>
            {personalizedBuckets.map((bucket) => (
                <div key={bucket.name} className={styles.forYouBucket}>
                    <div className={styles.bucketHeader}>
                        <Icon size="xlarge">
                            <TicketIcon />
                        </Icon>
                        <Typography as="h3">{bucket.name}</Typography>
                    </div>
                    <EventCardsBucket
                        label={bucket.name}
                        affCode="ebdssbehomepeforyou"
                        events={bucket.events}
                        isAuthenticated={isAuthenticated}
                        showSaveAction={isSaveStateReady}
                        showShareAction={true}
                    />
                </div>
            ))}
        </>
    );
};
